import type { ColumnDef } from '@tanstack/react-table';
import { ChevronRight, ExternalLink } from 'lucide-react';
import { QuotationStatusBadge } from './QuotationStatusBadge';
import type { QuotationListItemDto } from '../types/quotation.types';

function formatDate(value: string | null): string {
  if (!value) return '—';
  // Accept yyyy-MM-dd (from date fields) or full ISO (from createdAt/updatedAt).
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toISOString().slice(0, 10);
}

export const quotationColumns: ColumnDef<QuotationListItemDto>[] = [
  {
    accessorKey: 'quotationNumber',
    header: 'Quotation No.',
    cell: ({ row }) => (
      <span className="font-medium text-foreground">{row.original.quotationNumber}</span>
    ),
  },
  {
    accessorKey: 'customerName',
    header: 'Customer',
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span className="text-sm">{row.original.customerName || '—'}</span>
        {row.original.projectCategoryName && (
          <span className="text-xs text-muted-foreground">{row.original.projectCategoryName}</span>
        )}
      </div>
    ),
  },
  {
    accessorKey: 'projectName',
    header: 'Project',
    cell: ({ row }) => (
      <span className="text-sm">{row.original.projectName || '—'}</span>
    ),
  },
  {
    accessorKey: 'preparedByName',
    header: 'Prepared By',
    cell: ({ row }) => (
      <span className="text-sm">{row.original.preparedByName || '—'}</span>
    ),
  },
  {
    id: 'updatedAt',
    accessorKey: 'updatedAt',
    header: 'Last Updated',
    cell: ({ row }) => (
      <span className="text-sm tabular-nums text-muted-foreground">
        {formatDate(row.original.updatedAt ?? row.original.createdAt)}
      </span>
    ),
  },
  {
    accessorKey: 'totalLineItems',
    header: 'Items',
    cell: ({ row }) => (
      <span className="text-sm tabular-nums">{row.original.totalLineItems}</span>
    ),
  },
  {
    accessorKey: 'cuttingListNumber',
    header: 'Cutting List',
    cell: ({ row }) => {
      const cl = row.original.cuttingListNumber;
      const url = row.original.netSuiteUrl;
      if (!cl) return <span className="text-sm text-muted-foreground">—</span>;
      return url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm font-mono text-info underline underline-offset-2 decoration-info/40 hover:decoration-info"
          onClick={(e) => e.stopPropagation()}
        >
          {cl}
          <ExternalLink className="h-3 w-3" />
        </a>
      ) : (
        <span className="text-sm font-mono">{cl}</span>
      );
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => <QuotationStatusBadge status={row.original.status} />,
  },
  {
    id: 'chevron',
    header: '',
    cell: () => <ChevronRight className="h-4 w-4 text-muted-foreground" />,
  },
];
